import { useState, useRef, useEffect } from 'react';
import { apiClient, type MeResponse } from '@/lib/api/client';
import { cn } from '@/lib/utils';

interface Props {
  me: MeResponse;
  onSwitched: () => void;
  onCreate: () => void;
}

export function WorkspaceSwitcher({ me, onSwitched, onCreate }: Props) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState<number>(-1);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const active =
    me.workspaces.find((w) => w.ws_id === me.active_ws_id) ?? me.workspaces[0] ?? null;

  // When opening, default highlight to currently-active ws.
  useEffect(() => {
    if (!open) return;
    const idx = me.workspaces.findIndex((w) => w.ws_id === active?.ws_id);
    setHighlighted(idx >= 0 ? idx : 0);
  }, [open, me.workspaces, active?.ws_id]);

  // Move DOM focus to the highlighted option (keyboard navigation feel).
  useEffect(() => {
    if (!open || highlighted < 0) return;
    optionRefs.current[highlighted]?.focus();
  }, [open, highlighted]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function onKey(e: React.KeyboardEvent) {
    if (!open) return;
    const max = me.workspaces.length - 1;
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((i) => Math.min(max, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(0, i - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlighted(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlighted(max);
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (highlighted >= 0 && me.workspaces[highlighted]) {
        e.preventDefault();
        switchTo(me.workspaces[highlighted]!.ws_id);
      }
    }
  }

  async function switchTo(ws_id: string) {
    if (switching || ws_id === active?.ws_id) {
      setOpen(false);
      return;
    }
    setSwitching(ws_id);
    try {
      await apiClient.setActiveWorkspace(ws_id);
      onSwitched();
    } finally {
      setSwitching(null);
      setOpen(false);
    }
  }

  return (
    <div className="relative" ref={ref} onKeyDown={onKey}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:border-ring/60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="size-2 rounded-full bg-primary" aria-hidden />
        <span className="max-w-[200px] truncate">{active?.name ?? 'No workspace'}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" className="text-muted-foreground" aria-hidden>
          <path d="M2 4 L5 7 L8 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Switch workspace"
          aria-activedescendant={
            highlighted >= 0 && me.workspaces[highlighted]
              ? `ws-option-${me.workspaces[highlighted]!.ws_id}`
              : undefined
          }
          className="absolute right-0 z-20 mt-2 w-72 rounded-lg border border-border bg-popover p-1 shadow-2xl"
        >
          <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Workspaces
          </div>
          {me.workspaces.map((w, i) => (
            <button
              key={w.ws_id}
              id={`ws-option-${w.ws_id}`}
              ref={(el) => {
                optionRefs.current[i] = el;
              }}
              type="button"
              role="option"
              aria-selected={w.ws_id === active?.ws_id}
              onClick={() => switchTo(w.ws_id)}
              onMouseEnter={() => setHighlighted(i)}
              className={cn(
                'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent',
                w.ws_id === active?.ws_id && 'bg-accent',
              )}
              disabled={switching === w.ws_id}
            >
              <div className="min-w-0">
                <div className="truncate text-foreground">{w.name}</div>
                <div className="truncate font-mono text-[10px] text-muted-foreground">
                  /w/{w.slug} · {w.role}
                </div>
              </div>
              {switching === w.ws_id && (
                <span className="font-mono text-[10px] text-muted-foreground">…</span>
              )}
            </button>
          ))}
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onCreate();
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <span className="text-base leading-none">+</span> New workspace
          </button>
        </div>
      )}
    </div>
  );
}
